// @flow
// $FlowFixMe
import React, { useMemo, useLayoutEffect, useState, useEffect } from "react";
import { get, noop } from "lodash";
import { usePendingResults } from "@fdr/custom-hooks/ProgramPage";
import type { RaceProgram, RaceWagerType, Results } from "@tvg/types/Race";
import RaceOfficialsTemplate from "@fdr/atomic-ui/_templates/RaceOfficials";
import { type Actions, setBetType } from "@tvg/shared-actions/BetActions";
import type { UnaryFn } from "@tvg/types/Functional";
import mediator from "@tvg/mediator";
import { doesSilkLoads } from "@fdr/utils/raceUtils";
import {
  type Actions as ProgramPageActions,
  setLoadingRunners
} from "@fdr/shared-actions/ProgramPageActions";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import { graphql } from "@apollo/client/react/hoc";
import ApolloOptions from "./graphql/options.graph";
import RaceOfficialsQuery from "./graphql/queries/RaceResults.graphql";
import RaceResultsSubscription from "./graphql/queries/RaceResultsSubscription.graphql";

type Props = {
  race: RaceProgram,
  races: RaceProgram[],
  wagerProfile: string,
  wagerType: ?RaceWagerType,
  results: ?Results,
  dispatch: Dispatch<Actions | ProgramPageActions>,
  isLoading: boolean,
  subscribeToMore: UnaryFn<mixed, void>,
  hasWebSockets: boolean,
  showSilks: boolean
};

export const RaceOfficials = (props: Props) => {
  const [showSilksState, setShowSilksState] = useState(props.showSilks);
  const [unsubscribe, setUnsubscribe] = useState(null);

  const {
    race,
    results,
    races,
    dispatch,
    isLoading,
    subscribeToMore,
    wagerProfile,
    showSilks
  } = props;

  const isResultsPending = usePendingResults(get(race, "status.code"), results);

  useEffect(() => {
    (async () => {
      const bettingInterests = get(race, "bettingInterests", []);
      if (bettingInterests.length) {
        const result = await doesSilkLoads(
          get(race, "bettingInterests", []),
          true
        );
        setShowSilksState(showSilks && result);
      } else {
        setShowSilksState(false);
      }
    })();
  }, [JSON.stringify(race.bettingInterests), showSilks]);

  useLayoutEffect(() => {
    dispatch(setBetType(null));
    dispatch(setLoadingRunners(false));
  }, []);

  useEffect(() => {
    if (subscribeToMore && typeof unsubscribe !== "function") {
      const unsubscribefn = subscribeToMore({
        document: RaceResultsSubscription,
        variables: {
          trackAbbr: get(race, "track.trackCode", "").toUpperCase(),
          wagerProfile,
          raceNumber: +get(race, "raceNumber", ""),
          numOfRaces: 1
        }
      });

      setUnsubscribe(() => unsubscribefn);
    }
    return () => typeof unsubscribe === "function" && unsubscribe();
  }, [subscribeToMore]);

  const changeRace = (action: string, destinationUrl) => {
    mediator.base.dispatch({
      type: "RACE_RESULTS_CHANGE_RACE",
      payload: {
        actionName: action,
        destinationUrl,
        module: "Result"
      }
    });
  };

  return useMemo(
    () => (
      <RaceOfficialsTemplate
        raceTypeCode={get(race, "type.code", "T")}
        results={results}
        changeRace={changeRace}
        isResultsPending={isResultsPending}
        race={race}
        races={races}
        isLoading={isLoading}
        showSilks={showSilksState}
      />
    ),
    [
      isResultsPending,
      isLoading,
      showSilksState,
      showSilks,
      JSON.stringify(race),
      JSON.stringify(results),
      JSON.stringify(races)
    ]
  );
};

RaceOfficials.defaultProps = {
  race: {},
  races: [],
  results: null,
  wagerProfile: "FDR-Generic",
  wagerType: null,
  dispatch: noop,
  refetch: noop,
  isLoading: true,
  subscribeToMore: noop,
  hasWebSockets: false,
  showSilks: false
};

export default connect()(
  graphql(RaceOfficialsQuery, ApolloOptions)(RaceOfficials)
);
